'use strict';

import React from 'react';
import { ajax } from '@hms-dbmi-bgm/shared-portal-components/es/components/util';

import { fallbackCallback, DEFAULT_SELECT, getItemsFromPortal, getSelect } from './utils';

import {
    SUBMISSION_STATUS_TAGS,
    SUBMISSION_STATUS_DEFAULT_FILTER,
    CELL_CULTURE_MIXTURES,
} from './config';



class SubmissionStatusFilterComponent extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            submission_centers: [],
            filter: SUBMISSION_STATUS_DEFAULT_FILTER,
            assays: [],
            sequencers: [],
            donors: [],
            cell_culture_mixtures_and_tissues: [],
            cell_lines: [],
        };
    }

    // Does not includes tissues for now
    getSampleSourceCodes = (type, state_variable) => {
        ajax.load(
            `/search/?code%21=No+value&type=${type}&limit=300`,
            (resp) => {
                const res = resp['@graph'];
                const items = res.map((item) => {
                    return {
                        title: item.display_title,
                        code: item.code,
                    };
                });
                this.setState({
                    [state_variable]: items,
                });
            },
            'GET',
            fallbackCallback
        );
    };

    getTissuesAndMixtures = () => {
        getItemsFromPortal(
            'OntologyTerm',
            '&tags=tissue_type',
            200,
            (items) => {
                const primaryTissues = [];
                items.forEach((item) => {
                    primaryTissues.push(item.display_title);
                });
                primaryTissues.push("3AC - Fibroblast");
                const tissuesAndMixtures = [
                    ...primaryTissues.sort((a, b) => a.localeCompare(b)),
                    ...CELL_CULTURE_MIXTURES,
                ];
                const selectItems = tissuesAndMixtures.map((tissue) => ({
                    title: tissue,
                    code: tissue, // this is used in the search query. Here we need, e.g. "3AK - Brain, Frontal Lobe"
                }));
                this.setState({
                    cell_culture_mixtures_and_tissues: selectItems,
                });
            }
        );
    };

    componentDidMount() {
        getItemsFromPortal('SubmissionCenter', '', 100, (items) => {
            this.setState({ submission_centers: items });
        });

        getItemsFromPortal('Assay', '', 100, (items) => {
            this.setState({ assays: items });
        });

        getItemsFromPortal('Sequencer', '', 100, (items) => {
            this.setState({ sequencers: items });
        });

        getItemsFromPortal(
            'Donor',
            '&submission_centers.display_title=NDRI+TPC',
            200,
            (items) => {
                this.setState({ donors: items });
            }
        );
        this.getTissuesAndMixtures();
        this.getSampleSourceCodes('CellLine', 'cell_lines');
    }

    setFilter = (filterName, selection) => {
        const filter = JSON.parse(JSON.stringify(this.state.filter));
        filter[filterName] = selection;
        this.props.setFilter(filterName, selection);
        this.setState((prevState) => ({
            filter: filter,
        }));
    };

    getSubmissionCenterSelect = () => {
        const options = [
            <option value="all">All</option>,
            <option value="all_gcc">All GCCs</option>,
        ];
        const options_gcc = [];
        const options_other = [];

        this.state.submission_centers.forEach((sc) => {
            const op = <option value={sc.title}>{sc.title}</option>;
            if (sc.title.includes('GCC')) {
                options_gcc.push(op);
            } else {
                options_other.push(op);
            }
        });
        if (options_gcc.length > 0) {
            options.push(<optgroup label="GCCs">{options_gcc}</optgroup>);
        }
        if (options_other.length > 0) {
            options.push(<optgroup label="Other">{options_other}</optgroup>);
        }
        const defaultValue = 'all_gcc';
        const filterName = 'submission_center';
        return getSelect(options, defaultValue, filterName, this.setFilter);
    };

    getFilesetStatusSelect = () => {
        const options = [
            <option value="all">All</option>,
            <option value="in review">In Review</option>,
            <option value="open">Released to public</option>,
            <option value="released-network">Released to network</option>,
            <option value="released-public-network">
                Released to public or network
            </option>,
        ];
        const defaultValue = 'in review';
        const filterName = 'fileset_status';
        return getSelect(options, defaultValue, filterName, this.setFilter);
    };

    getAssaySelect() {
        if (this.state.assays == 0) {
            return DEFAULT_SELECT;
        }

        const options = [<option value="all">All</option>];
        this.state.assays.forEach((sc) => {
            // Escape special characters (e.g. "&" in "varCUT&Tag") so the value
            // survives the backend's urlencode/unquote round-trip when building
            // the search subrequest.
            options.push(
                <option value={encodeURIComponent(sc.title)}>{sc.title}</option>
            );
        });
        const defaultValue = 'all';
        const filterName = 'assay';
        return getSelect(options, defaultValue, filterName, this.setFilter);
    }

    getSequencerSelect() {
        if (this.state.sequencers == 0) {
            return DEFAULT_SELECT;
        }

        const options = [<option value="all">All</option>];
        this.state.sequencers.forEach((sc) => {
            // Escape special characters so the value survives the backend's
            // urlencode/unquote round-trip when building the search subrequest.
            options.push(
                <option value={encodeURIComponent(sc.title)}>{sc.title}</option>
            );
        });
        const defaultValue = 'all';
        const filterName = 'sequencer';
        return getSelect(options, defaultValue, filterName, this.setFilter);
    }

    getDonorSelect() {
        if (this.state.donors == 0) {
            return DEFAULT_SELECT;
        }

        const options = [<option value="all">All</option>];
        this.state.donors.forEach((d) => {
            options.push(<option value={d.title}>{d.title}</option>);
        });
        const defaultValue = 'all';
        const filterName = 'donor';
        return getSelect(options, defaultValue, filterName, this.setFilter);
    }

    getCellCultureMixtureAndTissueSelect() {
        if (this.state.cell_culture_mixtures_and_tissues == 0) {
            return DEFAULT_SELECT;
        }

        const options = [<option value="all">All</option>];
        this.state.cell_culture_mixtures_and_tissues.forEach((sc) => {
            options.push(<option value={sc.code}>{sc.title}</option>);
        });
        const defaultValue = 'all';
        const filterName = 'cell_culture_mixtures_and_tissues';
        return getSelect(options, defaultValue, filterName, this.setFilter);
    }

    getCellLineSelect() {
        if (this.state.cell_lines == 0) {
            return DEFAULT_SELECT;
        }

        const options = [<option value="all">All</option>];
        this.state.cell_lines.forEach((sc) => {
            options.push(<option value={sc.code}>{sc.code}</option>);
        });
        const defaultValue = 'all';
        const filterName = 'cell_line';
        return getSelect(options, defaultValue, filterName, this.setFilter);
    }

    getFilesetCreationInput = (filter_name) => {
        return (
            <React.Fragment>
                <input
                    type="date"
                    className="form-control"
                    onChange={(e) =>
                        this.setFilter(filter_name, e.target.value)
                    }
                />
            </React.Fragment>
        );
    };

    toggleTagFilter = (type, tag) => {
        const tags = this.state.filter[type];
        if (tags.includes(tag)) {
            const index = tags.indexOf(tag);
            tags.splice(index, 1);
        } else {
            tags.push(tag);
        }
        this.setFilter(type, tags);
    };

    getTagFilter = (type) => {
        return SUBMISSION_STATUS_TAGS.map((tag) => {
            const badgeType = this.state.filter[type].includes(tag)
                ? 'info'
                : 'lighter';
            const cn = 'badge clickable me-1 bg-' + badgeType;
            return (
                <div
                    className={cn}
                    onClick={() => this.toggleTagFilter(type, tag)}>
                    {tag}
                </div>
            );
        });
    };

    render() {
        return (
            <React.Fragment>
                <small className="text-muted text-uppercase">Filter</small>
                <div className="bg-light p-1">
                    <div className="row">
                        <div className="col-lg-3">
                            <div className="p-2">
                                Submission Center:{' '}
                                {this.getSubmissionCenterSelect()}
                            </div>
                            <div className="p-2">
                                FileSet Status: {this.getFilesetStatusSelect()}
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="p-2">
                                Assay: {this.getAssaySelect()}
                            </div>
                            <div className="p-2">
                                Sequencer: {this.getSequencerSelect()}
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="p-2">
                                Cell Line: {this.getCellLineSelect()}
                            </div>
                            <div className="p-2">
                                Cell Culture Mixture / Tissue:
                                {this.getCellCultureMixtureAndTissueSelect()}
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="p-2">
                                Donor: {this.getDonorSelect()}
                            </div>
                            <div className="p-2">
                                Metadata submitted - From/To:{' '}
                                <div className="d-flex flex-row">
                                    <div className="w-50">
                                        {this.getFilesetCreationInput(
                                            'fileset_created_from'
                                        )}
                                    </div>
                                    <div className="w-50">
                                        {this.getFilesetCreationInput(
                                            'fileset_created_to'
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="p-2">
                                <div>FileSet includes Tags:</div>
                                {this.getTagFilter('include_tags')}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="p-2">
                                <div>FileSet excludes Tags:</div>
                                {this.getTagFilter('exclude_tags')}
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}

export const SubmissionStatusFilter = React.memo(
    function SubmissionStatusFilter(props) {
        return <SubmissionStatusFilterComponent {...props} />;
    }
);
